import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy, RefreshCw } from "lucide-react";
import { Toast } from "../../components/Toast";

type OutputType = "paragraphs" | "sentences" | "words";

// Standard Lorem Ipsum word bank
const LOREM_WORDS = [
  "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
  "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
  "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
  "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
  "consequat", "duis", "aute", "irure", "in", "reprehenderit", "voluptate",
  "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "excepteur", "sint",
  "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
  "deserunt", "mollit", "anim", "id", "est", "laborum", "at", "vero", "eos",
  "accusamus", "iusto", "odio", "dignissimos", "ducimus", "blanditiis",
  "praesentium", "voluptatum", "deleniti", "atque", "corrupti", "quos", "dolores",
  "quas", "molestias", "excepturi", "obcaecati", "cupiditate", "provident",
  "similique", "mollitia", "animi", "eligendi", "optio", "cumque", "nihil",
  "impedit", "quo", "minus", "quod", "maxime", "placeat", "facere", "possimus",
  "omnis", "voluptas", "assumenda", "repellendus", "temporibus", "autem",
  "quibusdam", "officiis", "debitis", "aut", "rerum", "necessitatibus", "saepe",
  "eveniet", "voluptates", "repudiandae", "recusandae", "itaque", "earum",
  "hic", "tenetur", "sapiente", "delectus", "reiciendis", "maiores", "alias",
  "perferendis", "doloribus", "asperiores", "repellat",
];

const CLASSIC_OPENING = "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";

const getRandomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getRandomWord = (): string => {
  return LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)];
};

const generateWords = (count: number): string => {
  const words: string[] = [];
  for (let i = 0; i < count; i++) {
    words.push(getRandomWord());
  }
  return words.join(" ");
};

const generateSentence = (): string => {
  const wordCount = getRandomInt(8, 15);
  const words: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    words.push(getRandomWord());
  }
  // Capitalize first word
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(" ") + ".";
};

const generateParagraph = (): string => {
  const sentenceCount = getRandomInt(4, 8);
  const sentences: string[] = [];
  for (let i = 0; i < sentenceCount; i++) {
    sentences.push(generateSentence());
  }
  return sentences.join(" ");
};

const generateLoremIpsum = (
  type: OutputType,
  quantity: number,
  startWithLorem: boolean,
): string => {
  let result = "";

  switch (type) {
    case "paragraphs": {
      const paragraphs: string[] = [];
      for (let i = 0; i < quantity; i++) {
        paragraphs.push(generateParagraph());
      }
      result = paragraphs.join("\n\n");
      break;
    }
    case "sentences": {
      const sentences: string[] = [];
      for (let i = 0; i < quantity; i++) {
        sentences.push(generateSentence());
      }
      result = sentences.join(" ");
      break;
    }
    case "words": {
      result = generateWords(quantity);
      // Capitalize first word
      result = result.charAt(0).toUpperCase() + result.slice(1);
      break;
    }
  }

  // Optionally start with classic opening
  if (startWithLorem && result.length > 0) {
    if (type === "paragraphs") {
      const paragraphs = result.split("\n\n");
      paragraphs[0] = CLASSIC_OPENING + " " + paragraphs[0].split(". ").slice(1).join(". ");
      result = paragraphs.join("\n\n");
    } else if (type === "sentences") {
      const sentences = result.split(". ");
      sentences[0] = CLASSIC_OPENING.replace(".", "");
      result = sentences.join(". ");
      if (!result.endsWith(".")) result += ".";
    } else {
      // For words, just prepend the classic words
      const classicWords = "Lorem ipsum dolor sit amet";
      const existingWords = result.split(" ");
      const neededWords = Math.max(0, quantity - 5);
      result = classicWords + (neededWords > 0 ? " " + existingWords.slice(0, neededWords).join(" ") : "");
    }
  }

  return result;
};

export function LoremIpsumGenerator() {
  const { t } = useTranslation();
  const [outputType, setOutputType] = useState<OutputType>("paragraphs");
  const [quantity, setQuantity] = useState(3);
  const [startWithLorem, setStartWithLorem] = useState(true);
  const [output, setOutput] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleGenerate = useCallback(() => {
    const text = generateLoremIpsum(outputType, quantity, startWithLorem);
    setOutput(text);
  }, [outputType, quantity, startWithLorem]);

  const handleCopy = useCallback(async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setToastMessage(t("loremIpsumGenerator.copied"));
    } catch {
      setToastMessage(t("loremIpsumGenerator.copyFailed"));
    }
  }, [output, t]);

  const outputTypes: { type: OutputType; label: string }[] = [
    { type: "paragraphs", label: t("loremIpsumGenerator.paragraphs") },
    { type: "sentences", label: t("loremIpsumGenerator.sentences") },
    { type: "words", label: t("loremIpsumGenerator.words") },
  ];

  return (
    <div className="lorem-generator">
      <div className="lg-controls">
        <div className="lg-control-group">
          <label className="lg-label">{t("loremIpsumGenerator.outputType")}</label>
          <div className="lg-toggle-group">
            {outputTypes.map(({ type, label }) => (
              <button
                key={type}
                type="button"
                className={`lg-toggle-btn ${outputType === type ? "active" : ""}`}
                onClick={() => setOutputType(type)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="lg-control-group">
          <label className="lg-label">{t("loremIpsumGenerator.quantity")}</label>
          <input
            type="number"
            className="lg-input"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            min={1}
            max={100}
          />
        </div>

        <div className="lg-control-group">
          <label className="lg-checkbox">
            <input
              type="checkbox"
              checked={startWithLorem}
              onChange={(e) => setStartWithLorem(e.target.checked)}
            />
            <span>{t("loremIpsumGenerator.startWithLorem")}</span>
          </label>
        </div>

        <button
          type="button"
          className="lg-generate-btn"
          onClick={handleGenerate}
        >
          <RefreshCw size={18} />
          {t("loremIpsumGenerator.generate")}
        </button>
      </div>

      <div className="lg-output-section">
        <label className="lg-label">{t("loremIpsumGenerator.output")}</label>
        <textarea
          className="lg-textarea"
          value={output}
          readOnly
          placeholder={t("loremIpsumGenerator.outputPlaceholder")}
        />
        <button
          type="button"
          className="lg-copy-btn"
          onClick={handleCopy}
          disabled={!output}
        >
          <Copy size={16} />
          {t("loremIpsumGenerator.copy")}
        </button>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
